import {
  InboxOutlined,
  LinkOutlined,
  MessageOutlined,
  PhoneOutlined
} from "@ant-design/icons"
import { Button, Flex, Tooltip } from "antd"
import { type MenuItemType } from "antd/es/menu/interface"
import { type ColumnType } from "antd/es/table/interface"
import React from "react"

import { type LeadListItem } from "../../../types/types.ts"
import { RowActionsMenu } from "../../../../../components/EntityTable/RowActionsMenu.tsx"

interface Action extends MenuItemType {
  hidden?: boolean
}

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetLeadsColumnActionsProps {
  t: Translate
  editAllowed?: boolean
  archiveLabel: string
  onSelectRow: (record: LeadListItem, action: string) => void
  isMobile?: boolean
}

export const getLeadsColumnActions = ({
  t,
  editAllowed,
  archiveLabel,
  onSelectRow,
  isMobile
}: GetLeadsColumnActionsProps): ColumnType<LeadListItem> => {
  // const allowedActions = [editAllowed]

  const getItems = (record: LeadListItem) => {
    const menuItems: Action[] = [
      {
        key: "view_details",
        label: t("leadsTable.row.view"),
        icon: <LinkOutlined />,
        onClick: () => onSelectRow(record, "view_details")
      }
    ]
    if (editAllowed && !record.is_closed_lost && !record.is_project) {
      menuItems.push({
        key: "archive",
        label: archiveLabel,
        icon: <InboxOutlined />,
        danger: true,
        onClick: () => onSelectRow(record, "archive")
      })
    }

    return menuItems
  }

  // The design puts call and message beside the overflow menu. Both act on the
  // customer's own details and hand off to the OS, so a row with neither on
  // file renders the button disabled rather than silently doing nothing.
  const quickActions = (record: LeadListItem) => {
    const contact = record?.customer || record?.temporary_customer
    const phone = contact?.phone
    const email = contact?.email

    return (
      <>
        <Tooltip
          title={
            phone
              ? t("leadsTable.row.callNumber", { phone })
              : t("leadsTable.row.noPhone")
          }
        >
          <Button
            type="text"
            size="small"
            className="leads-row-action"
            aria-label={t("leadsTable.row.call")}
            disabled={!phone}
            icon={<PhoneOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              window.location.href = `tel:${phone}`
            }}
          />
        </Tooltip>
        <Tooltip
          title={
            email
              ? t("leadsTable.row.emailAddress", { email })
              : t("leadsTable.row.noEmail")
          }
        >
          <Button
            type="text"
            size="small"
            className="leads-row-action"
            aria-label={t("leadsTable.row.email")}
            disabled={!email}
            icon={<MessageOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              window.location.href = `mailto:${email}`
            }}
          />
        </Tooltip>
      </>
    )
  }

  return {
    title: t("leadsTable.columns.actions"),
    // hidden: !allowedActions.some(Boolean),
    fixed: "right",
    width: 100,
    className: "text-center",
    render: (_, record) => {
      const items = getItems(record)

      if (!items.length) return null

      if (isMobile) {
        return (
          <Flex gap={4}>
            {items.map((item) => (
              <Tooltip key={item?.key} title={item?.label}>
                <Button
                  type="link"
                  icon={item?.icon}
                  onClick={item?.onClick as any}
                />
              </Tooltip>
            ))}
          </Flex>
        )
      }

      return (
        <Flex gap={2} justify={"center"} align={"center"}>
          {quickActions(record)}
          <RowActionsMenu
            items={items}
            buttonProps={{
              type: "text",
              size: "small",
              className: "leads-row-action",
              "aria-label": t("leadsTable.row.more")
            }}
          />
        </Flex>
      )
    }
  }
}
